/**
 * DocumentoDownloadCard — F-006 Phase 5 (IMPL-P5-005).
 *
 * One card per document type (contratto, ricevuta PDF, ricevuta XML, fattura
 * SDI). State machine UI:
 *
 *  - state === 'available' + idle   → Button "Scarica"
 *  - state === 'unavailable'        → STATIC message ("La ricevuta sarà
 *                                      disponibile…"), NO polling, NO useEffect
 *                                      with interval. User must press the
 *                                      page-level "Aggiorna" button to retry.
 *  - state === 'requesting'         → Button disabled with Loader2
 *  - state === 'error'              → Alert + retry button
 *
 * Two API shapes:
 *  1. Provider-driven (the page wires the praticaId and the document info):
 *     `<DocumentoDownloadCard tipo praticaId info />`
 *  2. Explicit `state` (for tests):
 *     `<DocumentoDownloadCard tipo praticaId state="unavailable" />`
 */
import { AlertCircle, Download, FileText, Loader2 } from 'lucide-react';
import type { DocumentoInfo, FileType } from '@app/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useDocumentDownload } from '../hooks/useDocumentDownload';
import { formatDate } from '../lib/format';

export type DocumentoCardState = 'available' | 'unavailable' | 'requesting' | 'error';

const TIPO_LABEL: Record<FileType, string> = {
  CONTRATTO_PDF: 'Contratto (PDF)',
  RICEVUTA_PDF: 'Ricevuta AdE (PDF)',
  RICEVUTA_XML: 'Ricevuta AdE (XML)',
  FATTURA_SDI: 'Fattura SDI',
};

const TIPO_DESCRIPTION: Record<FileType, string> = {
  CONTRATTO_PDF: 'Contratto di locazione firmato.',
  RICEVUTA_PDF: 'Ricevuta di registrazione rilasciata dall’Agenzia delle Entrate.',
  RICEVUTA_XML: 'File XML telematico Entratel della ricevuta AdE.',
  FATTURA_SDI: 'Fattura elettronica emessa al cliente.',
};

interface DocumentoDownloadCardProps {
  tipo: FileType;
  praticaId: string;
  /** Documento metadata from the API. Optional when `state` is explicit. */
  info?: DocumentoInfo;
  /** Explicit state override — used by tests. */
  state?: DocumentoCardState;
}

function formatSize(bytes?: number): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentoDownloadCard({
  tipo,
  praticaId,
  info,
  state: stateOverride,
}: DocumentoDownloadCardProps) {
  // Always call the hook (Rules of Hooks). When `state` is explicitly passed,
  // we still drive the click handler through the hook so tests can verify
  // either flow.
  const { download, state: downloadState, reset } = useDocumentDownload(praticaId);

  // Effective state: explicit override (tests) → otherwise derived from
  // `info.available` + mutation state.
  let state: DocumentoCardState;
  if (stateOverride) {
    state = stateOverride;
  } else if (downloadState === 'requesting') {
    state = 'requesting';
  } else if (downloadState === 'error') {
    state = 'error';
  } else if (info?.available) {
    state = 'available';
  } else {
    state = 'unavailable';
  }

  const size = formatSize(info?.sizeBytes);
  const label = TIPO_LABEL[tipo];

  return (
    <Card data-testid={`documento-card-${tipo}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{TIPO_DESCRIPTION[tipo]}</p>

        {state === 'available' ? (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              {info?.lastModifiedAt ? (
                <>
                  Aggiornato il{' '}
                  <time dateTime={info.lastModifiedAt}>
                    {formatDate(info.lastModifiedAt)}
                  </time>
                </>
              ) : null}
              {size ? <> · {size}</> : null}
            </div>
            <Button
              type="button"
              onClick={() => download(tipo)}
              aria-label={`Scarica ${label}`}
            >
              <Download className="mr-1 h-4 w-4" aria-hidden="true" />
              Scarica
            </Button>
          </div>
        ) : null}

        {state === 'unavailable' ? (
          <div
            className="rounded-md border border-dashed bg-muted/30 p-3 text-sm text-muted-foreground"
            role="status"
          >
            <p>
              {tipo === 'RICEVUTA_PDF' || tipo === 'RICEVUTA_XML'
                ? 'La ricevuta sarà disponibile quando l’operatore completa la lavorazione. Riceverai un\'email di notifica.'
                : tipo === 'FATTURA_SDI'
                  ? 'La fattura sarà disponibile dopo l’emissione SDI. Riceverai un\'email di notifica.'
                  : 'Il documento non è ancora disponibile. Riceverai un\'email di notifica quando sarà pronto.'}
            </p>
          </div>
        ) : null}

        {state === 'requesting' ? (
          <Button type="button" disabled aria-label="Preparazione download in corso">
            <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />
            Preparazione…
          </Button>
        ) : null}

        {state === 'error' ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>Download non riuscito</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>Si è verificato un errore durante la preparazione del download.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  reset();
                  download(tipo);
                }}
              >
                Riprova
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
